'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from "next/image";
import { useRef } from 'react';

import Reveal from "@/components/animation/reveal";
import Cta from "@/components/cta/cta";
import Pin, { setPinsHidden, animatePinsDrop } from "@/components/icon/pin";
import Section from "@/components/section";

const PIN_LOCATIONS = [
  { x: 27, y: 18 }, // Idaho
  { x: 69, y: 20 }, // North Dakota
  { x: 10, y: 43 }, // California
  { x: 55, y: 51 }, // Colorado
  { x: 73, y: 74 }, // Texas
]

export default function AboutMap({ image, link, text, title }) {
  const mapRef = useRef(null);
  const pinsRef = useRef([]);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const pins = pinsRef.current.filter(Boolean);
      setPinsHidden(pins);

      ScrollTrigger.create({
        trigger: mapRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          animatePinsDrop(pins, { staggerFrom: 'start' });
        },
      });
    });
  }, []);

  return (
    <Section theme="main-light" className="pt-29.25 pb-13 lg:pt-43.75 lg:pb-19">
      <div className="grid-7 items-center mx-auto">
        <Reveal className="col-span-full max-w-98.25 mx-auto lg:col-span-3">
          <h2 className="heading-3-small max-w-73 mx-auto text-center lg:max-w-none lg:heading-3 lg:text-left">{title}</h2>
          <p className="mt-6 body-1-small text-center text-pretty xl:body-2 lg:mt-8 lg:max-w-82.75 lg:text-left">{text}</p>
          <Cta
            href={link.href}
            target={link.target}
            className="w-fit mx-auto mt-8 cta-big lg:mx-0"
            transitionName="overlay-in"
          >
            {link.label}
          </Cta>
        </Reveal>

        <div ref={mapRef} className="relative col-span-full w-[calc(100%+var(--outer-gutter)*2)] -ml-(--outer-gutter) mt-5 overflow-visible lg:col-span-4 lg:w-[calc(100%_+_var(--spacing)_*_35)] lg:mt-0 lg:ml-0">
          <Image unoptimized={true} className="block w-full aspect-ratio-[1500/1326]" {...image} alt={image.alt || "World map showing Ancestry's global coverage"} loading="lazy" />

          {PIN_LOCATIONS.map(({ x, y }, index) => (
            <Pin
              key={`about-map-pin-${index}`}
              ref={(el) => (pinsRef.current[index] = el)}
              className="absolute"
              top={y}
              left={x}
              large
            />
          ))}
        </div>
      </div>
    </Section>
  )
}
